import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchPythonEndpoint, queryClient } from 'react-query-config';

import { CustomField, ItemType } from '../../../@types/python.d';
import { useAuth } from '../../context/Authentication.context';
import useItemTypes from '../item-types/use-item-types';
import { useGlobalLocationFilter } from '../locations/use-location-filter.hook';
import { useTicketTypesForLocation } from '../ticket-types/use-ticket-type';
import {
	CustomFieldType,
	GROUP_CUSTOM_FIELDS_PARAM,
	GroupCustomFieldsResponse,
	invalidationTypesFor,
	seedCustomFieldsFromGroup,
	selectCustomFields,
} from './custom-field-collections';

interface GroupCustomFieldsQueryParams {
	_fields?: string;
}

type GroupResponse = GroupCustomFieldsResponse;

/**
 * Publish the fetched definitions onto the legacy Knockout group so the Durandal shell
 * renders the same data. Kept out of `custom-field-collections` deliberately: everything
 * there is pure, and this writes to `window`.
 */
const mirrorGroupFieldsToLegacy = (data: GroupCustomFieldsResponse) => {
	Object.keys(data).forEach((key) => {
		if (key.endsWith('Fields')) {
			const typedKey = key as keyof Omit<GroupCustomFieldsResponse, 'itemTypes'>;
			window.global.central.group[typedKey] = data[key as keyof GroupCustomFieldsResponse] as CustomField[];
		} else if (key === 'itemTypes') {
			window.global.central.group.itemTypes = data.itemTypes;
		}
	});
};

const useCustomFields = ({ type }: { type: CustomFieldType }) => {
	const authUser = useAuth();

	if (!authUser?.id) {
		throw new Error('User not authenticated');
	}

	const queryKey = ['customFields', type] as const;

	const { data = [] } = useQuery<CustomField[], Error, CustomField[], any>({
		queryKey,
		queryFn: fetchPythonEndpoint<GroupResponse, GroupCustomFieldsQueryParams>(
			`${authUser.id}/null/jwt/groups/${authUser.workspace.id}`,
			{ _fields: GROUP_CUSTOM_FIELDS_PARAM },
			'GET'
		),
		select: (data: GroupResponse) => {
			mirrorGroupFieldsToLegacy(data);

			return selectCustomFields(data, type);
		},
		initialData: () => seedCustomFieldsFromGroup(type, window.global.central.group),
		refetchOnWindowFocus: false,
		staleTime: Infinity,
	});

	return data;
};

export const refetchCustomFields = async (type: CustomFieldType) => {
	const fieldTypes = invalidationTypesFor(type);

	if (!fieldTypes) {
		// eslint-disable-next-line no-console -- We want to warn about this
		console.warn(`refetchCustomFields: Unknown custom field type "${type}". No cache invalidation performed.`);

		return Promise.resolve();
	}

	return Promise.all(fieldTypes.map((fieldType) => queryClient.invalidateQueries(['customFields', fieldType])));
};

export const useItemTypeCustomFields = ({ itemTypeId }: { itemTypeId?: string | undefined }) => {
	const { itemTypes } = useItemTypes();
	const customFields = useCustomFields({ type: 'items' });

	const itemTypeCustomFields = useMemo(() => {
		const itemType = itemTypes.find((itemType) => itemType.id === itemTypeId);
		return customFields.filter((field) =>
			itemType?.custom_field_visibility?.some(
				(visibility) => visibility.field_def_id === field.id && visibility.visible === true
			)
		);
	}, [customFields, itemTypeId, itemTypes]);

	return itemTypeId ? itemTypeCustomFields : customFields;
};

export const useTicketTypeCustomFields = ({ ticketTypeId }: { ticketTypeId?: string | undefined }) => {
	const globalLocation = useGlobalLocationFilter();
	const { data: ticketTypes } = useTicketTypesForLocation(globalLocation?._id);
	const customFields = useCustomFields({ type: 'tickets' });

	const ticketTypeCustomFields = useMemo(() => {
		if (!ticketTypes) {
			return [];
		}
		const ticketType = ticketTypes.find((ticketType) => ticketType.id === ticketTypeId);
		return customFields.filter((field) =>
			ticketType?.custom_field_visibility?.some(
				(visibility) => visibility.field_def_id === field.id && visibility.visible === true
			)
		);
	}, [customFields, ticketTypeId, ticketTypes]);

	return ticketTypeId ? ticketTypeCustomFields : customFields;
};

export default useCustomFields;
